/* eslint-disable camelcase */
import { TrackingProductType, TrackingTargetAudience } from '@vfde-sails/tracking';
import { JarvisHomebox } from '../interface';
import { TrackingOrder, TrackingProduct, TrackingProposition } from './interface';
import { TrackingProductLine, TrackingRetentionType } from './enum';
import { JarvisPriceType } from '../enum';

/**
 * Save vodafone siteStructure into a constant to preserve the original array on page load,
 * because DIP script overwrites VF.siteStructure every time we call tracking
 */
/* istanbul ignore next */
export const pageLevels = window.VF?.siteStructure
    ? [ ...window.VF?.siteStructure ]
    : [];

/**
 * Get hotline numbers for tracking
 * @returns {string[]}
 */
export const getHotlineNumbers = (): string[] => window.VF?.hotlineNumbers || [];

/**
 * Get a TrackingProduct from a JarvisHomebox
 * @param {JarvisHomebox} selected - selected product
 * @returns {TrackingProduct} result tracking product
 */
export const getTrackingProduct = (selected: JarvisHomebox): TrackingProduct => {
    const { value, type } = selected.price;
    const product: TrackingProduct = {
        sku: selected.id,
        name: selected.id,
        duration: +selected.duration,
        type: selected.productType,
        proposition: 1,
        units: 1,
    };

    if (type === JarvisPriceType.ONCE) {
        product.price_once = value.toString();
    } else {
        product.price_monthly = value.toString();
    }

    return product;
};

/**
 * Get the tracking offers from the options of a product
 * @param {JarvisHomebox} product - product to get the options from
 * @returns {TrackingProduct[]} an array of TrackingProduct
 */
export const getOffers = (product: JarvisHomebox): TrackingProduct[] => {
    const trackingOffers: TrackingProduct[] = [];

    for (const offer of product.options) {
        const trackingOffer: TrackingProduct = {
            sku: offer.id,
            name: offer.id,
            type: offer.trackingType,
            proposition: 1,
            units: 1,
        };

        if (offer.price.type === JarvisPriceType.ONCE) {
            trackingOffer.price_once = offer.price.value.toFixed(2);
        }

        if (offer.price.type === JarvisPriceType.MONTHLY) {
            trackingOffer.price_monthly = offer.price.value.toFixed(2);
        }

        if (offer.price.type === JarvisPriceType.STAIRWAY) {
            trackingOffer.duration = offer.price.stairway?.period;
            trackingOffer.price_monthly = offer.price.stairway?.value.toFixed(2);
        }

        trackingOffers.push(trackingOffer);
    }

    return trackingOffers;
};

/**
 * Get tracking aProducts object for the selected product
 * @param {JarvisHomebox} selected - selected product
 * @returns {TrackingProduct[]} TrackingProducts generated from the product
 */
export const getTrackingProducts = (selected: JarvisHomebox): TrackingProduct[] => ([
    // Selected product itself
    getTrackingProduct(selected),
    // Add discounts and services
    ...getOffers(selected),
]);

/**
 * Get tracking aPropositon object for the selected product.
 * @param {JarvisHomebox} selected - selected product
 * @param {boolean} isLoggedIn - whether customer is logged in or not
 * @returns {TrackingProposition} tracking proposition object
 */
export const getTrackingProposition = (
    selected: JarvisHomebox,
    isLoggedIn: boolean
): TrackingProposition => ({
    id: 1,
    name: selected.id,
    target_audience: TrackingTargetAudience.Consumer,
    retention: isLoggedIn ? TrackingRetentionType.NEW_OPTION : TrackingRetentionType.NEW_CONTRACT,
    product_type: selected.unity ? selected.productType + ' (unity)' : selected.productType,
    product_line: TrackingProductLine.P1,
    product_category: TrackingProductType.Tariff,
    units: 1,
});

/**
 * Get tracking oOrder object for the selecteed
 * @param {JarvisHomebox} product - selected product
 * @param {boolean} isLoggedIn - whether customer is logged in or not
 * @returns {TrackingOrder} tracking order object
 */
export const getTrackingOrder = (product: JarvisHomebox, isLoggedIn: boolean): TrackingOrder => ({
    aProducts: getTrackingProducts(product),
    aPropositions: [getTrackingProposition(product, isLoggedIn)],
});
